import React from 'react';
import { Link } from 'react-router-dom';

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
  cta: string;
  color: 'primary' | 'accent';
}

const cimmplePayExtraCostFeatures = new Set([
  'Tax compliance & filing',
]);

const pricingPlans: PricingPlan[] = [
  {
    name: 'CimmpleFlow',
    price: '$69',
    period: '/user/month',
    description: 'Billed annually. $77/user if billed monthly.',
    features: [
      'Quotations (VendorConnect™)',
      'Order Management',
      'Purchasing',
      'Job Management (with live tracking)',
      'Quality Control (live reporting)',
      'Reporting (with scheduling)',
      'Accounting (advanced)',
      'AI-powered invoice extraction (PDF & email)',
      'Timesheets (3rd party integration)',
      'Document Storage (version control)',
      'Per-user pricing',
      'Unlimited Jobs/Customers',
    ],
    highlighted: true,
    cta: 'Get Started',
    color: 'primary',
  },
  {
    name: 'CimmplePay',
    price: '$29',
    period: '/month',
    description: 'Plus $3 per employee payroll',
    features: [
      'Automated salary calculations',
      'Employee self-service portal',
      'Leave & attendance tracking',
      'Comprehensive reporting',
      'Unlimited employees',
      'Email support',
      '$3 per employee per payroll run',
      'Tax compliance & filing',
      'Biometric face-recognition attendance',
    ],
    highlighted: true,
    cta: 'Get Started',
    color: 'accent',
  },
];

const PricingSection: React.FC = () => {
  return (
    <section id="pricing" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-4">
            Pricing
          </span>
          <h2 className="section-title">
            Simple, transparent
            <br />
            <span className="gradient-text">pricing for everyone</span>
          </h2>
          <p className="section-subtitle mx-auto mt-4">
            Choose the plan that fits your needs. All plans include core features with no hidden fees.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.color === 'primary'
                  ? 'bg-gradient-to-b from-primary-100 dark:from-primary-500/10 to-white dark:to-dark-900/50 border-2 border-primary-500/50 shadow-xl shadow-primary-500/10'
                  : 'bg-gradient-to-b from-accent-100 dark:from-accent-500/10 to-white dark:to-dark-900/50 border-2 border-accent-500/50 shadow-xl shadow-accent-500/10'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Plan Header */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-display font-bold text-slate-900 dark:text-white">
                    {plan.price}
                  </span>
                  <span className="text-slate-500 dark:text-dark-400">{plan.period}</span>
                </div>
                <p className="text-slate-500 dark:text-dark-400 mt-2 text-sm">
                  {plan.description}
                </p>
              </div>

              {/* Features */}
              <ul className={`space-y-4 ${plan.name === 'CimmplePay' ? 'mb-3' : 'mb-8'}`}>
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-dark-300">
                    <svg
                      className={`w-5 h-5 flex-shrink-0 ${
                        plan.color === 'primary'
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-accent-600 dark:text-accent-400'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      {feature}
                      {plan.name === 'CimmplePay' && cimmplePayExtraCostFeatures.has(feature) && (
                        <sup className="text-accent-600 dark:text-accent-400 font-medium">*</sup>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {plan.name === 'CimmplePay' && (
                <p className="text-xs text-slate-500 dark:text-dark-400 mb-8 leading-relaxed">
                  <sup className="text-accent-600 dark:text-accent-400 font-medium">*</sup>{' '}
                  Available at additional cost.
                </p>
              )}

              {/* CTA */}
              <Link
                to="/contact"
                className={`block text-center w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                  plan.color === 'primary'
                    ? 'btn-primary'
                    : 'px-6 py-3 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-xl hover:from-accent-400 hover:to-accent-500 transform hover:scale-[1.02] hover:shadow-lg hover:shadow-accent-500/25 transition-all duration-300'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Full pricing link */}
        <div className="text-center mt-12">
          <p className="text-slate-600 dark:text-dark-400">
            Looking for a free trial or add-ons?{' '}
            <Link to="/pricing" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
              View full pricing
            </Link>
          </p>
        </div>

        {/* FAQ Link */}
        <div className="text-center mt-4">
          <p className="text-slate-600 dark:text-dark-400">
            Have questions?{' '}
            <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
              Contact our team
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
